import { HButton } from "@components/Button";
import { ApiConfig } from "@config/index";
import { FormControl, FormControlLabel, Grid, MenuItem, RadioGroup, SvgIcon, Switch, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { apiClient } from "@services/baseAxios";
import { COLORS } from "@shared/constants/colors";
import { errorMessages } from "@shared/constants/messages";
import { LabelControl, RadioLabel, Title } from "@shared/styledComponents";
import { storeOpenSnackbar, storeSetGlobalLoading } from "@store/globalReducer";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Column, PaginatedResult } from "types/common";
import AddIcon from '@mui/icons-material/Add';
import { Input } from "@components/Input";
import { HSelect } from "@components/Select";
import { HDatePicker } from "@components/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { CustomRadio } from "@components/Radio";
import { storeSetAllGroupArticle } from "@store/directoryReducer";
import { HTable } from "@components/Table";
import { rowCustomHandlerType } from "@components/Table/constant";
import { ReactComponent as EditIcon } from '@assets/icons/icon-edit-gray.svg';
import { ArticleForm } from "./ArticleForm";

const Wrapper = styled('div')(() => ({
    width: '100%',
    height: '100%',
    backgroundColor: COLORS.GhostWhite,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column'
}));

const ToolbarWrapper = styled('div')(() => ({
    padding: "16px 0px"
}));

const columns: readonly Column[] = [
    {
        id: 'STT',
        label: 'STT',
        align: 'left',
        minWidth: 30,
        transform: (data: any) => (data.index + 1)
    },
    {
        id: 'Title',
        label: 'Tiêu đề bài viết',
        minWidth: 140,
    },
    {
        id: 'ArticleGroupName',
        label: 'Danh mục',
        minWidth: 120,
        align: 'left',
    },
    {
        id: 'CreatedDate',
        label: 'Ngày tạo',
        align: 'center',
        minWidth: 120,
        transform: (data: any) => (data.value ? moment(data.value).format("DD/MM/YYYY") : '--/--/----')
    },
    {
        id: 'Actions',
        align: 'center',
        label: 'Tác vụ',
        minWidth: 80,
        transform: (value: any) => (
            <div className="flex justify-center items-center">
                <Switch
                    checked={value.row.IsActive}
                    onChange={() => value.onCustomHandler(rowCustomHandlerType.TOGGLE)}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
                <Tooltip title="Chi tiết" placement="top">
                    <div onClick={() => value.onCustomHandler(rowCustomHandlerType.EDIT)} className='flex justify-center items-center cursor-pointer'>
                        <SvgIcon sx={{ width: 16, height: 16, color: COLORS.Green }} component={EditIcon} inheritViewBox />
                    </div>
                </Tooltip>
            </div>
        )
    },
];

export const ArticleAdministration = () => {
    const dispatch = useDispatch();
    const { allGroupArticle } = useSelector((state: any) => state.directory);

    const [isCreateNew, setIsCreateNew] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [radioValue, setRadioValue] = useState('-1');
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [selectedArticleGroup, setSelectedArticleGroup] = useState('-1');
    const [keyword, setKeyword] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [tableLoading, setTableLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState({} as any);
    const [isOpenArticleForm, setIsOpenArticleForm] = useState(false);
    const [paginatedData, setPaginatedData] = useState<PaginatedResult>({
        ListOut: [],
        TotalCount: 0,
        PageStart: 1,
        PageEnd: 1,
        TotalPage: 0
    } as PaginatedResult);

    const getAllGroupArticle = async () => {
        if (allGroupArticle?.length > 0) return;

        const { AppCode, Data } = await apiClient.get(ApiConfig.GET_ALL_GROUP_ARTICLE);
        if (AppCode === 200) {
            dispatch(storeSetAllGroupArticle(Data));
        }
        else {
            dispatch(storeSetAllGroupArticle([]));
        }
    }

    const handleChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const getObjectFilter = () => {
        var fromDateVNTime = dayjs(fromDate).add(7, 'hours') || null;
        var toDateVNTime = dayjs(toDate).add(7, 'hours') || null;

        return {
            Keyword: keyword,
            PageIndex: page + 1,
            PageSize: rowsPerPage,
            DateFrom: fromDateVNTime,
            DateTo: toDateVNTime,
            IsActive: radioValue === "-1" ? null : radioValue === "0" ? false : true,
            ArticleGroup: selectedArticleGroup === "-1" ? null : selectedArticleGroup
        };
    }
    const handleChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRadioValue((event.target as HTMLInputElement).value);
    };

    const onRowCustomHandler = (row: any, handlerType: any) => {
        setSelectedRow(row);
        if (handlerType === rowCustomHandlerType.EDIT) {
            setIsCreateNew(false);
            setIsOpenArticleForm(true);
        }
        else if (handlerType === rowCustomHandlerType.TOGGLE) {
            handleToggleActiveArticle(row);
        }
    }

    const handleToggleActiveArticle = async (row: any) => {
        try {
            dispatch(storeSetGlobalLoading(true));
            var { AppCode } = await apiClient.put(`${ApiConfig.ARTICLE}/${row.Id}/active`);
            dispatch(storeSetGlobalLoading(false));
            if (AppCode === 200) {
                dispatch(storeOpenSnackbar({ open: true, message: "Bật/Tắt thông báo bài viết thành công", type: "success" }));
                getArticlePaging();
            }
            else if (AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: "Bật/Tắt thông báo bài bài viết thất bại", type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: "Bật/Tắt thông báo bài bài viết thất bại", type: "error" }));
            }
        } catch (error: any) {
            dispatch(storeSetGlobalLoading(false));
            if (error?.response?.data?.AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: error?.response?.data?.Message, type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        }
    }

    const handleChangeArticleGroup = (event: { target: { value: string } }) => {
        setSelectedArticleGroup(event.target.value);
    };

    const handleCloseForm = () => {
        setIsOpenArticleForm(false);
        setSelectedRow({});
    }

    const handleSubmitSuccess = () => {
        handleCloseForm();
        dispatch(storeOpenSnackbar({ open: true, message: isCreateNew ? "Tạo bài viết thành công" : "Sửa bài viết thành công", type: "success" }));
        getArticlePaging();
    }

    const getArticlePaging = async () => {
        try {
            setTableLoading(true);
            var { AppCode, Data } = await apiClient.post(`${ApiConfig.ARTICLE}/paging`, getObjectFilter());
            setTableLoading(false);
            if (AppCode === 200) {
                setPaginatedData((prevState: PaginatedResult) => ({
                    ...prevState,
                    ...Data
                }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        } catch (error: any) {
            setTableLoading(false);
            if (error?.response?.data?.AppCode === 500) {
                dispatch(storeOpenSnackbar({ open: true, message: error?.response?.data?.Message, type: "error" }));
            }
            else {
                dispatch(storeOpenSnackbar({ open: true, message: errorMessages.SYSTEM_ERROR, type: "error" }));
            }
        }
    }

    useEffect(() => {
        getAllGroupArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        getArticlePaging();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, keyword, selectedArticleGroup, fromDate, toDate, radioValue])

    return (
        <Wrapper>
            <div className="w-full flex justify-between">
                <Title>Danh sách bài viết</Title>
                <HButton startIcon={<AddIcon />} onClick={() => {
                    setIsCreateNew(true);
                    setIsOpenArticleForm(true);
                }}>Tạo bài viết</HButton>
            </div>
            <ToolbarWrapper>
                <Grid container>
                    {/* Row 1 */}
                    <Grid sx={{ paddingRight: '8px' }} item xs={6}>
                        <FormControl sx={{ width: '100%' }} variant="standard">
                            <LabelControl>Tiêu đề bài viết</LabelControl>
                            <Input onChange={handleChangeKeyword} value={keyword} fullWidth placeholder="Tìm kiếm theo tiêu đề bài viết" />
                        </FormControl>
                    </Grid>
                    <Grid sx={{ paddingRight: '8px' }} item xs={3}>
                        <FormControl sx={{ width: '100%' }} variant="standard">
                            <LabelControl>Từ ngày</LabelControl>
                            <HDatePicker
                                value={fromDate}
                                ampm={false}
                                inputFormat="DD/MM/YYYY"
                                onChange={(newValue: any) => {
                                    setFromDate(newValue);
                                }}>
                            </HDatePicker>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl sx={{ width: '100%' }} variant="standard">
                            <LabelControl>Đến ngày</LabelControl>
                            <HDatePicker
                                value={toDate}
                                placeholder="Từ ngày"
                                inputFormat="DD/MM/YYYY"
                                ampm={false}
                                onChange={(newValue: any) => {
                                    setToDate(newValue);
                                }}>
                            </HDatePicker>
                        </FormControl>
                    </Grid>
                    {/* Row 2 */}
                    <Grid sx={{ paddingRight: '8px', marginTop: '16px' }} item xs={6}>
                        <FormControl sx={{ width: '100%' }} variant="standard">
                            <LabelControl>Danh mục</LabelControl>
                            <HSelect value={selectedArticleGroup}
                                onChange={handleChangeArticleGroup}>
                                <MenuItem key={-1} value={"-1"}>
                                    Tất cả
                                </MenuItem>
                                {
                                    allGroupArticle.map((data: any) => (<MenuItem key={data.Id} value={data.Id}>{data.Name}</MenuItem>))
                                }
                            </HSelect>
                        </FormControl>
                    </Grid>
                    <Grid sx={{ marginTop: '16px' }} item xs={6}>
                        <FormControl sx={{ paddingLeft: '12px' }} variant="standard">
                            <LabelControl>Trạng thái</LabelControl>
                            <RadioGroup
                                row
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                value={radioValue}
                                onChange={handleChangeRadio}
                            >
                                <FormControlLabel value="-1" control={<CustomRadio />} label={<RadioLabel>Tất cả</RadioLabel>} />
                                <FormControlLabel value="1" control={<CustomRadio />} label={<RadioLabel>Kích hoạt</RadioLabel>} />
                                <FormControlLabel value="0" control={<CustomRadio />} label={<RadioLabel>Chưa kích hoạt</RadioLabel>} />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>
            </ToolbarWrapper>
            <HTable
                columns={columns}
                pagingInfo={{
                    rowsPerPage,
                    page,
                    totalCount: paginatedData.TotalCount,
                    handleChangePage,
                    handleChangeRowsPerPage
                }}
                loading={tableLoading}
                listData={paginatedData.ListOut}
                onRowCustomHandler={onRowCustomHandler}
            ></HTable>
            {
                isOpenArticleForm &&
                <ArticleForm
                    isOpen={isOpenArticleForm}
                    isCreateNew={isCreateNew}
                    title={isCreateNew ? "Tạo bài viết" : "Sửa bài viết"}
                    data={selectedRow}
                    handleOnClose={handleCloseForm}
                    handleOnConfirm={handleSubmitSuccess}
                >
                </ArticleForm>
            }
        </Wrapper>
    )
}